/**
 * Impact components.
 *
 * The store's cumulative tree count lives in the GoodAPI app (formerly
 * "Sprout"), which exposes it at /app/badges/tree_count as an HTML fragment
 * containing <span class="spt-impact-banner-total">164082</span>.
 *
 * The endpoint sends Access-Control-Allow-Origin: *, so we read it directly
 * instead of mounting the app's own banner and scraping the rendered DOM.
 *
 * Every component reads the total through ImpactTotal, which caches per
 * endpoint in sessionStorage and collapses concurrent callers onto a single
 * request. Two sections on the same page therefore cost one fetch, and repeat
 * page views within the TTL cost none — the "updated hourly" the copy promises.
 *
 * Sections always render a server-side number first, so a failed or slow
 * request leaves the merchant-set fallback on screen rather than a blank slot.
 */

const ImpactTotal = (() => {
  const inFlight = {};
  const cacheKey = (endpoint) => `impact-counter:${endpoint}`;

  function read(endpoint, ttl) {
    try {
      const raw = sessionStorage.getItem(cacheKey(endpoint));
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || typeof entry.total !== "number") return null;
      if (Date.now() - entry.at > ttl) return null;
      return entry.total;
    } catch (error) {
      return null;
    }
  }

  function write(endpoint, total) {
    try {
      sessionStorage.setItem(cacheKey(endpoint), JSON.stringify({ total, at: Date.now() }));
    } catch (error) {
      /* private mode / quota — the total still works, it just refetches */
    }
  }

  /** Pull the integer out of the badge fragment without executing any of it. */
  function parse(html) {
    const el = new DOMParser().parseFromString(html, "text/html").querySelector(".spt-impact-banner-total");
    if (!el) return null;
    const digits = (el.textContent || "").replace(/[^\d]/g, "");
    return digits ? parseInt(digits, 10) : null;
  }

  return {
    /** Resolves with the total, or null when it cannot be read. Never rejects. */
    get(endpoint, ttl) {
      const cached = read(endpoint, ttl);
      if (cached !== null) return Promise.resolve(cached);

      if (!inFlight[endpoint]) {
        inFlight[endpoint] = fetch(endpoint, { headers: { Accept: "text/html" } })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((html) => {
            const total = parse(html);
            if (total !== null) write(endpoint, total);
            return total;
          })
          .catch(() => null)
          .then((total) => {
            delete inFlight[endpoint];
            return total;
          });
      }

      return inFlight[endpoint];
    },
  };
})();

const impactSettings = (el) => ({
  endpoint: el.dataset.endpoint,
  ttl: (parseInt(el.dataset.ttl, 10) || 3600) * 1000,
});

const impactFormat = (value) => value.toLocaleString("en-US");

/**
 * <impact-counter>
 *
 *   <impact-counter data-endpoint="..." data-ttl="3600">
 *     <span data-impact-count>164,082</span>
 *   </impact-counter>
 *
 * Sections that show copy derived from the total (e.g. "that's X trips around
 * the planet") listen for the bubbling `impact-counter:total` event rather than
 * re-reading the DOM.
 */
class ImpactCounter extends HTMLElement {
  connectedCallback() {
    this.target = this.querySelector("[data-impact-count]");
    const { endpoint, ttl } = impactSettings(this);
    if (!this.target || !endpoint) return;

    ImpactTotal.get(endpoint, ttl).then((total) => {
      if (total !== null) this.render(total);
    });
  }

  render(total) {
    const from = parseInt((this.target.textContent || "").replace(/[^\d]/g, ""), 10);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced || !from || from === total) {
      this.target.textContent = impactFormat(total);
    } else {
      this.countUp(from, total);
    }

    this.dispatchEvent(new CustomEvent("impact-counter:total", { detail: { total }, bubbles: true }));
  }

  countUp(from, to) {
    const duration = 900;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.target.textContent = impactFormat(Math.round(from + (to - from) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }
}

customElements.define("impact-counter", ImpactCounter);

/**
 * <impact-milestones>
 *
 * Turns the same total into community milestone progress: percentage of the
 * goal, how many trees are left, the bar width, and each tier's state.
 *
 * Liquid renders all of it from the fallback count first; this only refreshes
 * it with the live figure. Tier state is expressed as data-state on the card
 * and styled in CSS, never as inline styles from here — the one exception is
 * the bar width, which is a genuinely dynamic value.
 */
class ImpactMilestones extends HTMLElement {
  connectedCallback() {
    const { endpoint, ttl } = impactSettings(this);
    if (!endpoint) return;

    this.goal = parseInt(this.dataset.goal, 10) || 0;
    this.cards = Array.from(this.querySelectorAll("[data-milestone]"));

    ImpactTotal.get(endpoint, ttl).then((total) => {
      if (total !== null) this.render(total);
    });
  }

  render(total) {
    this.renderProgress(total);
    this.renderCards(total);
  }

  renderProgress(total) {
    if (!this.goal) return;

    const percent = Math.min(Math.round((total / this.goal) * 100), 100);
    const toGo = Math.max(this.goal - total, 0);

    const percentEl = this.querySelector("[data-milestone-percent]");
    if (percentEl) percentEl.textContent = `${percent}%`;

    const toGoEl = this.querySelector("[data-milestone-togo]");
    if (toGoEl) toGoEl.textContent = impactFormat(toGo);

    const bar = this.querySelector("[data-milestone-bar]");
    if (bar) {
      bar.style.transform = `scaleX(${percent / 100})`;
      bar.parentElement.setAttribute("aria-valuenow", percent);
    }
  }

  /** Unlocked once reached; the lowest target still ahead is the one in progress. */
  renderCards(total) {
    let next = null;
    this.cards.forEach((card) => {
      const target = parseInt(card.dataset.target, 10);
      if (target > total && (next === null || target < next)) next = target;
    });

    const labels = {
      unlocked: this.dataset.labelUnlocked || "Unlocked",
      progress: this.dataset.labelProgress || "In progress",
      locked: this.dataset.labelLocked || "Locked",
    };

    this.cards.forEach((card) => {
      const target = parseInt(card.dataset.target, 10);
      const state = target <= total ? "unlocked" : target === next ? "progress" : "locked";

      card.setAttribute("data-state", state);
      const statusEl = card.querySelector("[data-milestone-status]");
      if (statusEl) statusEl.textContent = labels[state];
    });
  }
}

customElements.define("impact-milestones", ImpactMilestones);

/**
 * <impact-leaderboard>
 *
 * Two independent toggles — scope (cities/states) and window (24h/7d/30d) —
 * selecting one of six panels rendered server-side by Liquid. Nothing is
 * fetched here: switching is pure show/hide, so every combination is already
 * in the HTML and works before this script runs.
 */
class ImpactLeaderboard extends HTMLElement {
  connectedCallback() {
    this.scope = this.dataset.scope || "cities";
    this.window = this.dataset.window || "7d";

    this.panels = Array.from(this.querySelectorAll("[data-panel]"));
    this.scopeButtons = Array.from(this.querySelectorAll("[data-scope]"));
    this.windowButtons = Array.from(this.querySelectorAll("[data-window]"));

    this.bind(this.scopeButtons, "scope");
    this.bind(this.windowButtons, "window");
    this.sync();
  }

  bind(buttons, key) {
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this[key] = button.dataset[key];
        this.sync();
      });
    });
  }

  sync() {
    const selected = `${this.scope}:${this.window}`;
    this.panels.forEach((panel) => panel.classList.toggle("hidden", panel.dataset.panel !== selected));

    this.mark(this.scopeButtons, "scope", this.scope);
    this.mark(this.windowButtons, "window", this.window);
  }

  mark(buttons, key, value) {
    buttons.forEach((button) => {
      const active = button.dataset[key] === value;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }
}

customElements.define("impact-leaderboard", ImpactLeaderboard);

/**
 * <impact-countdown data-until="2026-08-07T03:00:00Z">
 *   <span data-countdown>12:03:41</span>
 * </impact-countdown>
 *
 * Counts to an absolute instant rather than a recurring local-time schedule,
 * which is why this exists alongside <resetting-countdown-timer>. That one
 * builds its target in the visitor's own timezone, so a shopper in Buenos
 * Aires and one in California would count towards different moments — fine for
 * a per-visitor promotion, wrong for a board that resets once for everybody.
 *
 * The instant comes from the job that builds the board, so the reset rule lives
 * in one place instead of being reimplemented here.
 */
class ImpactCountdown extends HTMLElement {
  connectedCallback() {
    this.target = this.querySelector("[data-countdown]");
    this.until = Date.parse(this.dataset.until);
    if (!this.target || Number.isNaN(this.until)) return;

    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const remaining = this.until - Date.now();

    // Past the instant the payload was built for, the job has not run yet.
    // Roll forward a day rather than showing a negative or frozen zero.
    if (remaining <= 0) {
      this.until += 24 * 60 * 60 * 1000;
      return;
    }

    const total = Math.floor(remaining / 1000);
    const pad = (value) => String(value).padStart(2, "0");
    this.target.textContent = [
      pad(Math.floor(total / 3600)),
      pad(Math.floor((total % 3600) / 60)),
      pad(total % 60),
    ].join(":");
  }
}

customElements.define("impact-countdown", ImpactCountdown);

/**
 * <impact-badges>
 *
 * Liquid decides every badge it can from the counts published on the
 * metaobject. Only the ones measured against the all-time tree total have to
 * wait, because that figure lives in GoodAPI rather than in the payload — so
 * this re-checks those once it arrives, using the same cached total as the hero
 * counter.
 *
 * Badges that unlock on anything else, or that are switched on by hand, are
 * left exactly as rendered.
 */
class ImpactBadges extends HTMLElement {
  connectedCallback() {
    const { endpoint, ttl } = impactSettings(this);
    this.badges = Array.from(this.querySelectorAll('[data-badge][data-metric="trees_total"]'));
    if (!endpoint || !this.badges.length) return;

    ImpactTotal.get(endpoint, ttl).then((total) => {
      if (total !== null) this.render(total);
    });
  }

  render(total) {
    const labels = {
      unlocked: this.dataset.labelUnlocked || "Unlocked",
      locked: this.dataset.labelLocked || "Locked",
    };

    this.badges.forEach((badge) => {
      const threshold = parseInt(badge.dataset.threshold, 10);
      const state = Number.isNaN(threshold) || total >= threshold ? "unlocked" : "locked";

      badge.setAttribute("data-state", state);
      const status = badge.querySelector("[data-badge-status]");
      if (status) status.textContent = labels[state];
    });
  }
}

customElements.define("impact-badges", ImpactBadges);
